import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Download, FileText, Shield } from 'lucide-react';
import axios from 'axios';
import { SEOHead } from '@/components/SEOHead';
import { AnalyticalField } from '@/components/AnalyticalField';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GridBg = () => (
  <div className="absolute inset-0 grid-texture pointer-events-none" aria-hidden="true" />
);

export function Procurement() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', organization: '', email: '', role: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleRequest = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/procurement/access`, form);
      setSubmitted(true);
    } catch {
      setError('Request failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const docs = [
    { label: 'Engagement Scope Document', desc: '', icon: <FileText size={16} className="text-amber-500" /> },
    { label: 'Data Handling & Retention Policy', desc: '', icon: <Shield size={16} className="text-amber-500" /> },
    { label: 'Security Posture Summary', desc: '', icon: <Lock size={16} className="text-amber-500" /> },
    { label: 'Standard MSA Template', desc: '', icon: <Download size={16} className="text-amber-500" /> },
    { label: 'Mutual NDA Template', desc: '', icon: <FileText size={16} className="text-amber-500" /> },
  ];

  return (
    <div className="bg-zinc-950">
      <SEOHead />
      <section className="relative section-border pt-10 pb-6 md:pt-12 md:pb-8 overflow-hidden">
        <GridBg />
        <AnalyticalField variant="quiet" focalPosition="0.80,0.18" intensity={0.10} />
        <div className="container-site relative z-10">
          <div className="border-b border-zinc-800 pb-12 mb-12">
            <span className="text-xs font-mono tracking-widest uppercase text-amber-500 block mb-5">Procurement</span>
            <h1 className="font-heading text-4xl md:text-4xl md:text-5xl font-medium tracking-tight text-zinc-100 leading-tight">Procurement Pack</h1>
            <p className="mt-6 text-base text-zinc-400 max-w-2xl leading-relaxed">
              Version 1.1
              <br />
              Last Updated: December 2025
              <br />
              <br />
              Records for security, privacy and enterprise procurement review.
              <br />
              Access to procurement materials is restricted to authorized representatives via formal request submission.
              <br />
              This document outlines the operational, commercial and data governance boundaries of OnPoint.Works engagements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-16">
            <div>
              <h2 className="font-heading text-xl font-medium text-zinc-100 mb-6">Security &amp; Privacy Posture</h2>
              <div className="space-y-4">
                {[
                  { label: 'Service Model', value: 'Restricted external analytical services, limited to a defined engagement under signed contract.' },
                  { label: 'Data Access Model', value: 'File-based review; no system access, no API access, no production access, no credential storage.' },
                  { label: 'Analysis Environment', value: 'Isolated analytical environment; no external SaaS routing or shared production systems.' },
                  { label: 'Data Storage Controls', value: 'Encrypted storage during retention window; access restricted to engagement scope.' },
                  { label: 'Data Retention', value: 'Maximum 14-day retention post-delivery; secure deletion confirmed in writing.' },
                  { label: 'Data Transmission', value: 'Secure file transfer only; no consumer-grade sharing platforms.' },
                  { label: 'Third-Party Involvement', value: 'No subprocessors; no data sharing, resale, or redistribution.' },
                  { label: 'Jurisdiction', value: 'Avyria LLC, New Mexico - USA.' },
                ].map((item) => (
                  <div key={item.label} className="flex gap-4 py-3 border-b border-zinc-800/50 last:border-0">
                    <span className="text-xs font-mono text-zinc-600 w-32 flex-shrink-0 mt-0.5">{item.label}</span>
                    <span className="text-sm text-zinc-400">{item.value}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-xl py-6 font-medium text-zinc-100 mb-1">Information Security Controls Matrix</p>
              <div className="space-y-4">
                {[
                  { label: 'Access Control', value: 'Restricted to defined analytical scope' },
                  { label: 'Environment Isolation', value: 'Isolated analysis on offline Linux environment' },
                  { label: 'Credential Management', value: 'No persistent credentials retained' },
                  { label: 'Data Transmission', value: 'Secure transfer only' },
                  { label: 'Data Storage', value: 'LUKS2-encrypted during retention window' },
                  { label: 'Secure Deletion', value: 'Documented deletion confirmation' },
                  { label: 'Third-Party Routing', value: 'No SaaS routing or subprocessors' },
                  { label: 'Commercial Boundary', value: 'Limited strictly to signed SOW scope' },
                ].map((item) => (
                  <div key={item.label} className="flex gap-4 py-3 border-b border-zinc-800/50 last:border-0">
                    <span className="text-xs font-mono text-zinc-600 w-36 flex-shrink-0 mt-0.5">{item.label}</span>
                    <span className="text-sm text-zinc-400">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl font-medium text-zinc-100 mb-6">Compliance & Commercial Review Overview</h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                OnPoint.Works provides independent analytical services under a defined commercial and legal framework. No persistent system integration or ongoing processing relationship is established.
              </p>
              <p className="text-sm text-zinc-400 leading-relaxed mb-3">Vendor Classification</p>
              <ul className="space-y-3">
                {[
                  'Independent external analytical service provider',
                  'Operates strictly under signed Statement of Work (SOW)',
                  'No embedded or integrated system role',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/50 mt-2 flex-shrink-0" />
                    <span className="text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6 mb-3">Commercial Structure</p>
              <ul className="space-y-3">
                {[
                  'Single-use engagement model',
                  'No recurring billing',
                  'No subscription or licensing obligations',
                  'Standard MSA available upon request',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/50 mt-2 flex-shrink-0" />
                    <span className="text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6 mb-3">Confidentiality Standard</p>
              <ul className="space-y-3">
                {[
                  'Engagements executed under Mutual NDA',
                  'Confidentiality applies to all submitted materials and outputs',
                  'No use of client data beyond defined analytical purpose',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/50 mt-2 flex-shrink-0" />
                    <span className="text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6 mb-3">Processing Boundary</p>
              <p className="text-sm text-zinc-400 leading-relaxed mb-3">OnPoint.Works does not:</p>
              <ul className="space-y-3">
                {[
                  'Modify client systems',
                  'Deploy code',
                  'Access production environments',
                  'Establish ongoing data processing relationships',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/50 mt-2 flex-shrink-0" />
                    <span className="text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-zinc-400 leading-relaxed mt-4">Clients retain full ownership of submitted materials and outputs.</p>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6 mb-3">Risk Classification Statement</p>
              <p className="text-sm text-zinc-400 leading-relaxed mb-3">OnPoint.Works qualifies as a:</p>
              <ul className="space-y-3">
                {[
                  'Low Operational Impact Vendor',
                  'No System Access Vendor',
                  'Non-Persistent Processing Provider',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/50 mt-2 flex-shrink-0" />
                    <span className="text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6 mb-3">The service:</p>
              <ul className="space-y-3">
                {[
                  'Does not integrate with internal systems',
                  'Does not access live production environments',
                  'Does not deploy executable code',
                  'Does not create continuous data pipelines',
                  'Does not retain data beyond defined retention window',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500/50 mt-2 flex-shrink-0" />
                    <span className="text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-zinc-400 leading-relaxed mt-4">Risk exposure is limited to temporary file-based analytical review within a bounded scope.</p>
            </div>
          </div>

          {/* Document pack */}
          <div className="mb-12">
            <h2 className="font-heading text-xl font-medium text-zinc-100 mb-2">Procurement Document Pack</h2>
            <p className="text-sm text-zinc-500 mb-6">Documentation is available to authorized procurement and compliance representatives upon request. Materials are delivered via secure access link within one business day.</p>
            <p className="text-sm text-zinc-500 mb-6">Available documentation:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {docs.map((doc) => (
                <div key={doc.label} className="flex items-start gap-4 border border-zinc-800 rounded-md p-5 bg-zinc-900/20">
                  <div className="flex-shrink-0 mt-0.5">{doc.icon}</div>
                  <div>
                    <div className="text-sm font-medium text-zinc-200 mb-1">{doc.label}</div>
                    <div className="text-xs text-zinc-500">{doc.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-xl font-medium text-zinc-100 mb-2">Document Control Notice</h2>
            <p className="text-sm text-zinc-500 mb-6">This Procurement Pack summary reflects the current operational and commercial model of OnPoint.Works at the version and date indicated above. Controls apply exclusively within the scope of each signed engagement. In the event of conflict, executed contractual agreements (SOW/MSA) prevail.</p>

            {/* Access request form */}
            {submitted ? (
              <div data-testid="procurement-success" className="border border-zinc-700 rounded-md p-6 bg-zinc-900/30">
                <p className="text-sm text-zinc-300 font-medium mb-1">Request received.</p>
                <p className="text-sm text-zinc-500">You will receive the procurement pack via email within 3 business days.</p>
              </div>
            ) : (
              <div className="border border-zinc-800 rounded-md p-6 bg-zinc-900/20">
                <h3 className="text-xl font-medium text-zinc-100 mb-5">Request Procurement Pack Access</h3>
                <form onSubmit={handleRequest} data-testid="procurement-form" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-1.5">Name <span className="text-amber-500">*</span></label>
                      <input value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Enkel Schenkel" data-testid="procurement-name" required className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 rounded-md px-4 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-1.5">Organization <span className="text-amber-500">*</span></label>
                      <input value={form.organization} onChange={(e) => update('organization', e.target.value)} placeholder="Acme Corp" data-testid="procurement-organization" required className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 rounded-md px-4 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-1.5">Business Email <span className="text-amber-500">*</span></label>
                      <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="jane@acme.com" data-testid="procurement-email" required className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 rounded-md px-4 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="block text-xs font-mono tracking-widest uppercase text-zinc-300 mb-1.5">Role <span className="text-amber-500">*</span></label>
                      <input value={form.role} onChange={(e) => update('role', e.target.value)} placeholder="Security Reviewer / Procurement Manager" data-testid="procurement-role" required className="w-full bg-zinc-900 border border-zinc-800 focus:border-amber-500/50 rounded-md px-4 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-600 outline-none transition-colors duration-200" />
                    </div>
                  </div>
                  {error && <p data-testid="procurement-error" className="text-sm text-memo-warn">{error}</p>}
                  <button type="submit" data-testid="procurement-submit-btn" disabled={!form.name || !form.organization || !form.email || !form.role || submitting} className="inline-flex items-center gap-2 px-5 py-2.5 bg-zinc-100 text-zinc-900 rounded-md text-sm font-medium hover:bg-white transition-colors duration-200 disabled:opacity-80 disabled:cursor-not-allowed">
                    {submitting ? 'Submitting...' : 'Request Access'}
                    <Download size={14} />
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}